import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { GridConstants } from '@/common/grid/GridConstants';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { DateUtils } from '@/common/utils/DateUtils';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { ObjUtils } from '@/common/utils/ObjUtils';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * GP-9: This is a step in the Calc Job Dialog Wizard.
 * The user may select specific products for the calculation (if none are selected all eligible products will be included)
 */
export const ProdSelectStep = ({ calcChainReq, setCalcChainReq, setNextStep, setStepReady }) => {
  const rsrc = useRsrc();

  const [hasAggs, setHasAggs] = useState();
  const [isDerived, setIsDerived] = useState();
  const [isRealloc, setIsRealloc] = useState();

  const [subtitle, setSubtitle] = useState();
  const [prodSelectedCount, setProdSelectedCount] = useState(0);
  const [prodSetExists, setProdSetExists] = useState(calcChainReq.prodSets?.length > 0 ? true : null)
  const [isProdSet, setIsProdSet] = useState(calcChainReq.prodSets?.length > 0);

  const liabPolType = calcChainReq.liabPolType;
  const polId = calcChainReq.calcJobReqs[0].polId;
  const isRepro = calcChainReq.calcJobReqs[0].src !== 'PER';

  const earliestStartDate = Math.min(
    ...calcChainReq.calcJobReqs.map(j => DateUtils.periodStartDate(j.period.code) ?? '99999999')
  );

  useEffect(() => {
    // Prod Sets exist
    if (prodSetExists == null) {
      const rowsReq = {
        entity: 'PROD',
        replaceParams: {
          polType: liabPolType.code,
          polId: polId == null ? '' : polId,
          earliestPeriodStartDate: earliestStartDate,
          prodType: 'PROD_SET',
          domain: 'INT',
        }
      };

      // TODO: Confirm this logic with customer that has prod sets
      rsrc.rowCount('md', 'PROD_LIST', rowsReq).then(res => {
        setProdSetExists(res > 0);
      });
    }

    // Agg Check
    if (isRepro) {
      setHasAggs(false);
    } else {
      const polIds = calcChainReq.calcJobReqs.map(calcJobReq => calcJobReq.polId);
      const aggRowsReq = {
        expr: ExprUtils.whereParams('polId', polIds, 'type', 'AGG')
      };

      rsrc.rows('gp', 'POL_FORMULA', aggRowsReq).then((aggFormulas => {
        setHasAggs(aggFormulas.length > 0);
      }));

    }

    // Pol Types
    const derRowsReq = {
      enumCode: 'POL_TYPE',
      filterMethod: 'derivedPolTypes'
    }
    rsrc.data('gp', 'ENUM_LIST_DATA_PLUGIN', derRowsReq).then((derivedPols => {
      const polCodes = derivedPols.map(pol => pol.code);
      const derived = polCodes.includes(liabPolType.code);
      setIsDerived(derived);

      if (!derived) {
        const reallocRowsReq = {
          enumCode: 'POL_TYPE',
          filterMethod: 'reallocPolTypes'
        }

        rsrc.data('gp', 'ENUM_LIST_DATA_PLUGIN', reallocRowsReq).then((reallocPols => {
          const polCodes = reallocPols.map(pol => pol.code);
          const realloc = polCodes.includes(liabPolType.code);
          setIsRealloc(realloc);
        }));
      }
    }));
  }, []);


  // Let parent know what the next page is
  useEffect(() => {
    if (hasAggs) {
      setNextStep('Overrides');
    } else if (isDerived) {
      setNextStep('Dependencies');
    } else if (isRealloc) {
      setNextStep('Reallocations');
    } else {
      setNextStep('Options');
    }

  }, [isProdSet, hasAggs, isDerived, isRealloc])



  const augFieldDefs = (fieldToFieldDef) => {
    if (isProdSet) {
      ObjUtils.clearObj(fieldToFieldDef)

      fieldToFieldDef.checkbox = GridConstants.checkbox;
      fieldToFieldDef.name = {
        field: 'name'
      };
      return;
    }

    const columns = ['name', 'upps', 'shelfLifeExpDate'];

    Object.keys(fieldToFieldDef).forEach(field => {
      if (!columns.includes(field)) {
        delete fieldToFieldDef[field];
      }
    });

    fieldToFieldDef.checkbox = GridConstants.checkbox;

    fieldToFieldDef.ndc11 = {
      field: 'ndc11',
      displayName: 'NDC11',
    }

    fieldToFieldDef.ndc9 = {
      field: 'ndc9',
      displayName: 'NDC9',
    }

    fieldToFieldDef.marketEntryDate = {
      field: 'marketEntryDate',
      precedence: 10000,
      type: 'DATE'
    }
  };

  const entityListRef = useRef();
  const clientSideRowData = useCallback((setDataSource) => {
    if (setStepReady) setStepReady(false);
    const query = isProdSet ? 'PROD_LIST' : 'POL_TYPE_ELIG_PRODS';

    const earliestStartDate = Math.min(
      ...calcChainReq.calcJobReqs.map(j => DateUtils.periodStartDate(j.period.code) ?? '99999999')
    );

    const rowsReq = {
      replaceParams: {
        polType: liabPolType.code,
        polId: polId == null ? '' : polId,
        earliestPeriodStartDate: earliestStartDate,
        prodType: 'PROD_SET',
        domain: 'INT',
      }
    };

    rsrc.rows('md', query, rowsReq).then((rows) => {
      if (liabPolType.code === 'BUNDLE') {
        rsrc.details('gp', 'POL', polId).then((details) => {
          const bundleNdcs = details.generalProps.filter(prop => prop.name === 'NDCS').map(prop => prop.value.split(','));
          const allowedNdcs = bundleNdcs[0] || [];
          setDataSource(rows.filter(row => allowedNdcs.includes(row.ndc11)));
          if (setStepReady) setStepReady(true);
        })
      } else {
        setDataSource(rows);
        if (setStepReady) setStepReady(true);
      }

      const gridRef = entityListRef.current.gridRef.current;
      const gridApiRef = gridRef.gridApiRef.current;
      const selectedProds = isProdSet ? calcChainReq.prodSets : calcChainReq.prods;

      gridApiRef.forEachNode((node) => {
        const prodMatchId = isProdSet ? node.data?.id : node.data?.ndc11;
        if (selectedProds.includes(prodMatchId)) {
          node.setSelected(true);
        }
      });
    })
  }, [isProdSet]);

  const rowSelected = (event) => {
    const selectedRows = event.api.getSelectedRows();
    setProdSelectedCount(selectedRows.length);

    if (!isProdSet) {
      const selectedProds = selectedRows.map(row => row.ndc11);
      setCalcChainReq(prev => ({
        ...prev,
        prods: selectedProds
      }));
    } else {
      const selectedProdSetIds = selectedRows.map(row => row.id);
      setCalcChainReq(prev => ({
        ...prev,
        prodSets: selectedProdSetIds
      }));
    }
  };

  useEffect(() => {
    if (liabPolType.code === 'BUNDLE') {
      setSubtitle('Products included in this bundle.');
    } else {
      const prodWord = prodSelectedCount === 1 ?
        (isProdSet ? 'product set' : 'product') :
        (isProdSet ? 'product sets' : 'products');

      const article = prodSelectedCount === 1 ? 'is' : 'are';

      if (prodSelectedCount === 0) {
        setSubtitle(
          `Select ${prodWord} to be calculated or skip ahead for regulation products. ` +
          `If no ${prodWord}  are selected, all eligible ${prodWord} will be automatically included.`
        );
      } else {
        setSubtitle(
          `Select products to be calculated or skip ahead for regulation products. ` +
          `There ${article} currently ${prodSelectedCount} ${prodWord} selected.`
        );
      }
    }
  }, [prodSelectedCount, isProdSet]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          my: 1,
          gap: 10
        }}>
        <Typography variant="h6">{subtitle}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* All Policies Toggle */}
          <ToggleButtonGroup
            value={isProdSet ? 'set' : 'prods'}
            exclusive
            disabled={!prodSetExists}
            onChange={(e, value) => {
              setIsProdSet(value === 'set');
              setProdSelectedCount(0);
              setCalcChainReq(prev => ({ ...prev, prods: [], prodSets: [] }));
            }}
            sx={{ height: 40, color: 'primary.main' }}>
            <ToggleButton value="prods" data-test-id="toggle-prods" sx={{ whiteSpace: 'nowrap' }}>
              Products
            </ToggleButton>
            <ToggleButton value="set" data-test-id="toggle-prod-sets" sx={{ whiteSpace: 'nowrap' }}>
              Product Sets
            </ToggleButton>
          </ToggleButtonGroup>

        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ flex: 1, minHeight: 10, overflow: 'auto', width: '100%' }}>
        <EntityList
          data-test-id="dialog-product-select"
          key={`${isProdSet}`}
          module="md"
          entity="NDC11_PROD"
          ref={entityListRef}
          augFieldDefs={augFieldDefs}
          clientSideRowData={clientSideRowData}
          gridOptions={{
            rowSelection: 'multiple',
          }}
          events={{
            onRowSelected: rowSelected,
          }}
        />
      </Box>
    </Box >
  )
}